import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './SaldoBarJogador.css';

/**
 * Página para Jogador consultar seu saldo negativo de consumo BAR
 */

const SaldoBarJogador = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rodadas, setRodadas] = useState([]);
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [consumos, setConsumos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    carregarRodadas();
  }, [user, navigate]);

  useEffect(() => {
    if (rodadaSelecionada && user) {
      carregarConsumos();
    }
  }, [rodadaSelecionada, user]);

  const carregarRodadas = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      // Incluir rodadas abertas E fechadas para permitir visualizar saldo durante a rodada
      const todasRodadas = Array.isArray(rodadasRes.data) 
        ? rodadasRes.data.filter(r => r.status === 'aberta' || r.status === 'fechada')
        : [];

      // Ordenar: rodadas abertas primeiro, depois fechadas (mais recentes primeiro)
      todasRodadas.sort((a, b) => {
        if (a.status === 'aberta' && b.status === 'fechada') return -1;
        if (a.status === 'fechada' && b.status === 'aberta') return 1;
        return new Date(b.data) - new Date(a.data);
      });

      setRodadas(todasRodadas);

      if (todasRodadas.length > 0 && !rodadaSelecionada) {
        setRodadaSelecionada(todasRodadas[0].id);
      }

      setError('');
    } catch (err) {
      console.error('Erro ao carregar rodadas:', err);
      setError(err.response?.data?.error || 'Erro ao carregar rodadas');
    } finally {
      setLoading(false);
    }
  };

  const carregarConsumos = async () => {
    if (!rodadaSelecionada || !user) return;

    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(`/consumos-bar/jogador/${user.id}/rodada/${rodadaSelecionada}`);
      setConsumos(response.data);
    } catch (err) {
      console.error('Erro ao carregar consumos:', err);
      if (err.response?.status === 404) {
        // Se não encontrou participação, significa que o jogador não participou desta rodada
        // ou não tem consumos registrados
        setConsumos({ saldo_bar: 0, saldo_bar_pago: false, consumos: [] });
        setError(''); // Não é um erro, apenas não há dados
      } else {
        setError(err.response?.data?.error || 'Erro ao carregar consumos');
        setConsumos({ saldo_bar: 0, saldo_bar_pago: false, consumos: [] });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !rodadas.length) {
    return <div className="container">Carregando...</div>;
  }

  const saldoBar = consumos ? parseFloat(consumos.saldo_bar || 0) : 0;
  const rodadaSelecionadaObj = rodadas.find(r => r.id === rodadaSelecionada);

  return (
    <div className="container saldo-bar-jogador-container">
      <div className="page-header">
        <h1>🍺 Meu Saldo BAR</h1>
        <button onClick={() => navigate('/dashboard-jogador')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="form-group rodada-selector">
        <label>Selecione a Rodada:</label>
        <select
          value={rodadaSelecionada}
          onChange={(e) => setRodadaSelecionada(e.target.value)}
          className="select-rodada"
        >
          {rodadas.length > 0 ? (
            rodadas.map(rodada => (
              <option key={rodada.id} value={rodada.id}>
                {new Date(rodada.data).toLocaleDateString('pt-BR')} - {rodada.status === 'aberta' ? '🟢 Aberta' : '🔴 Fechada'}
              </option>
            ))
          ) : (
            <option value="">Nenhuma rodada disponível</option>
          )}
        </select>
      </div>

      {rodadaSelecionadaObj && (
        <>
          {rodadaSelecionadaObj.status === 'aberta' && (
            <div className="info-box" style={{ 
              background: '#e7f3ff', 
              border: '1px solid #2196F3', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '20px' 
            }}>
              <p style={{ margin: 0, color: '#1976D2' }}>
                <strong>ℹ️ Rodada Aberta:</strong> O saldo pode ser atualizado conforme novos consumos forem registrados pelo admin.
              </p>
            </div>
          )}
          {consumos !== null ? (
            <>
              <div className="saldo-card">
                <h2>Saldo BAR</h2>
                <p className={`saldo-valor ${
                  saldoBar === 0 
                    ? 'zero' 
                    : (consumos && consumos.saldo_bar_pago === true) 
                      ? 'pago' 
                      : 'pendente'
                }`}>
                  R$ {saldoBar.toFixed(2)}
                </p>
                {saldoBar > 0 && consumos && consumos.saldo_bar_pago === true && (
                  <p className="saldo-ok">
                    ✅ Saldo quitado! Não há pendências.
                  </p>
                )}
                {saldoBar > 0 && consumos && (!consumos.saldo_bar_pago || consumos.saldo_bar_pago === false) && (
                  <p className="saldo-aviso">
                    ⚠️ Você possui saldo devedor de consumo BAR nesta rodada.
                  </p>
                )}
                {saldoBar === 0 && (
                  <p className="saldo-ok">
                    ✅ Você não possui saldo devedor nesta rodada.
                  </p>
                )}
              </div>

              {consumos.consumos && consumos.consumos.length > 0 && (
                <div className="consumos-section">
                  <h3>Detalhamento de Consumos</h3>
                  <table className="consumos-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Quantidade</th>
                        <th>Preço Unitário</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consumos.consumos.map((consumo) => (
                        <tr key={consumo.id}>
                          <td>{consumo.produto?.nome || 'Produto'}</td>
                          <td>{consumo.quantidade}</td>
                          <td>R$ {parseFloat(consumo.preco_unitario || 0).toFixed(2)}</td>
                          <td>R$ {parseFloat(consumo.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          Total:
                        </td>
                        <td style={{ fontWeight: 'bold' }}>
                          R$ {saldoBar.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {(!consumos.consumos || consumos.consumos.length === 0) && saldoBar === 0 && (
                <p className="sem-consumos">Você não possui consumos registrados nesta rodada.</p>
              )}
            </>
          ) : (
            <div className="saldo-card">
              <h2>Carregando...</h2>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SaldoBarJogador;
